/**
 * Player Icons Collection
 * 
 * This file contains the collection of SVG icons that players can choose from
 * in the game. These icons are displayed next to player names in the waiting room,
 * during gameplay, and on the winners podium.
 */

export interface PlayerIcon {
  id: number;
  svg: string; // SVG content as a string
  name: string;
  color: string; // Hex color code for reference
}

/**
 * Collection of player icon options
 * Each icon has a unique ID, SVG content as a string, a name, and a color
 * 
 * The SVG content should use viewBox="0 0 24 24" to maintain consistent sizing
 */

export const playerIcons: PlayerIcon[] = [
  {
    id: 0,
    name: "Worker",
    color: "#FFD700",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
      .wa-face-10 { fill: #F5DEB3; stroke: #A0522D; stroke-width: 1; }
      .wa-hair-10 { fill: #222222; }
      .wa-eyes-10 { fill: #000; }
      .wa-mouth-10 { fill: none; stroke: #000; stroke-width: 1; }
      .wa-helmet-10 { fill: #FFD700; stroke: #B8860B; stroke-width: 1; }
    </style>
    <path class="wa-face-10" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
    <path class="wa-hair-10" d="M12 2a10 10 0 0 1 10 10H2a10 10 0 0 1 10-10z"/>
    <circle class="wa-eyes-10" cx="8" cy="10" r="1.5"/>
    <circle class="wa-eyes-10" cx="16" cy="10" r="1.5"/>
    <path class="wa-mouth-10" d="M8 16a6 6 0 0 0 8 0"/>
    <path class="wa-helmet-10" d="M12 4a6 6 0 0 0-6 6h12a6 6 0 0 0-6-6z"/>
    </svg>`,
  },
  {
    id: 1,
    name: "Baron Von Bloop",
    color: "#4CAF50",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-14 { fill: #4CAF50; }
      .wa-eye-white-14 { fill: white; }
      .wa-eye-iris-14 { fill: red; }
      .wa-eye-pupil-14 { fill: black; }
      .wa-mouth-14 { fill: #222; }
      .wa-antenna-14 { stroke: gray; stroke-width: 2; }
      .wa-antenna-tip-14 { fill: yellow; }
    </style>
    <rect class="wa-head-14" x="25" y="10" width="50" height="80" rx="10"/>
    <circle class="wa-eye-white-14" cx="50" cy="40" r="15"/>
    <circle class="wa-eye-iris-14" cx="50" cy="40" r="8"/>
    <circle class="wa-eye-pupil-14" cx="50" cy="40" r="3"/>
    <rect class="wa-mouth-14" x="35" y="70" width="30" height="5"/>
    <line class="wa-antenna-14" x1="50" y1="10" x2="50" y2="0"/>
    <circle class="wa-antenna-tip-14" cx="50" cy="0" r="4"/>
</svg>`,
  },
  {
    id: 2,
    name: "Professor Alt",
    color: "#A98467",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
        .wa-face-5 {
            fill: #F2D7EE;
            stroke: #D4A373;
            stroke-width: 1.5;
        }
        .wa-hair-5 {
            fill: #A98467;
        }
        .wa-eyes-5 {
            fill: #000;
            stroke: none;
        }
        .wa-glasses-5 {
            fill: none;
            stroke: #588157;
            stroke-width: 1;
        }
        .wa-mustache-5 {
            fill: #795548;
            stroke: none;
        }
        .wa-hat-5 {
            fill: #4A1E00;
            stroke: #3D0C02;
            stroke-width: 1;
        }
    </style>
    <path class="wa-face-5" d="M12 2C8 2 5 5 5 9v6c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-5" d="M5 9c0-2 3-4 7-4s7 2 7 4v2H5v-2z"/>
    <circle class="wa-eyes-5" cx="9" cy="12" r="1"/>
    <circle class="wa-eyes-5" cx="15" cy="12" r="1"/>
    <path class="wa-mustache-5" d="M9 14s1.5-1 3-1 3 1 3 1"/>
    <path class="wa-glasses-5" d="M8 11h3"/>
    <path class="wa-glasses-5" d="M13 11h3"/>
    <path class="wa-glasses-5" d="M11.5 11v2"/>
    <path class="wa-hat-5" d="M12 4a5 5 0 0 1 5 5h-10a5 5 0 0 1 5-5z"/>
</svg>`,
  },
  {
    id: 3,
    name: "The Blob",
    color: "#00FFFF",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-body-19 { fill: #00FFFF; }
      .wa-eyes-19 { fill: black; }
      .wa-mouth-19 { fill: none; stroke: black; stroke-width: 2; }
    </style>
    <path class="wa-body-19" d="M 20 80 C 5 60, 10 30, 40 20 C 70 10, 95 30, 80 80 Q 50 95, 20 80 Z"/>
    <circle class="wa-eyes-19" cx="40" cy="50" r="5"/>
    <circle class="wa-eyes-19" cx="65" cy="45" r="7"/>
    <path class="wa-mouth-19" d="M 45 70 Q 55 75, 65 65"/>
</svg>`,
  },
  {
    id: 4,
    name: "Cyberpunk",
    color: "#F72585",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
        .wa-face-1 {
            fill: #F72585;
            stroke: #9D4EDD;
            stroke-width: 1.5;
        }
        .wa-hair-1 {
            fill: #4CC9F0;
        }
        .wa-eyes-1 {
            fill: #FFF;
            stroke: #000;
            stroke-width: 0.5;
        }
        .wa-hat-1 {
            fill: #4361EE;
            stroke: #3A0CA3;
            stroke-width: 1;
        }
        .wa-shirt-1 {
          fill: #F72585;
          stroke: #9D4EDD;
          stroke-width: 1;
        }
    </style>
    <path class="wa-face-1" d="M12 2C8 2 5 5 5 9v8c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-1" d="M5 9c0-2 3-4 7-4s7 2 7 4v3H5v-3z"/>
    <circle class="wa-eyes-1" cx="9" cy="13" r="1.5"/>
    <circle class="wa-eyes-1" cx="15" cy="13" r="1.5"/>
    <path d="M 10 17 a 1 1 0 0 0 4 0" stroke="#000" stroke-width="1"/>
    <path class="wa-hat-1" d="M12 4a5 5 0 0 1 5 5h-10a5 5 0 0 1 5-5z"/>
</svg>`,
  },
  {
    id: 5,
    name: "Madame Zaza",
    color: "#FFC0CB",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-face-13 { fill: #FFC0CB; }
      .wa-hair-13 { fill: #8A2BE2; }
      .wa-wink-13 { stroke: black; stroke-width: 2; fill: none; }
      .wa-eye-13 { fill: black; }
      .wa-mouth-13 { stroke: #D81B60; stroke-width: 3; fill: none; stroke-linecap: round; }
      .wa-beautymark-13 { fill: black; }
    </style>
    <ellipse class="wa-face-13" cx="50" cy="55" rx="40" ry="45"/>
    <path class="wa-hair-13" d="M 10 50 Q 50 0, 90 50 C 90 20, 10 20, 10 50"/>
    <path class="wa-wink-13" d="M 30 45 Q 40 50, 50 45"/>
    <circle class="wa-eye-13" cx="70" cy="45" r="5"/>
    <path class="wa-mouth-13" d="M 40 75 C 45 80, 55 80, 60 75"/>
    <circle class="wa-beautymark-13" cx="30" cy="70" r="2"/>
</svg>`,
  },
  {
    id: 6,
    name: "Captain Clank",
    color: "#B0C4DE",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-18 { fill: #B0C4DE; }
      .wa-visor-plate-18 { fill: #4682B4; }
      .wa-visor-slit-18 { stroke: #FF4500; stroke-width: 4; }
      .wa-mouth-grill-18 { stroke: #2F4F4F; stroke-width: 3; }
      .wa-rivets-18 { fill: #2F4F4F; }
    </style>
    <rect class="wa-head-18" x="15" y="15" width="70" height="70" rx="8"/>
    <rect class="wa-visor-plate-18" x="25" y="30" width="50" height="20"/>
    <line class="wa-visor-slit-18" x1="30" y1="40" x2="70" y2="40"/>
    <line class="wa-mouth-grill-18" x1="35" y1="65" x2="65" y2="65"/>
    <line class="wa-mouth-grill-18" x1="35" y1="70" x2="65" y2="70"/>
    <line class="wa-mouth-grill-18" x1="35" y1="75" x2="65" y2="75"/>
    <circle class="wa-rivets-18" cx="18" cy="18" r="2"/>
    <circle class="wa-rivets-18" cx="82" cy="18" r="2"/>
    <circle class="wa-rivets-18" cx="18" cy="82" r="2"/>
    <circle class="wa-rivets-18" cx="82" cy="82" r="2"/>
</svg>`,
  },
  {
    id: 7,
    name: "Cowboy",
    color: "#A0522D",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
      .wa-face-11 { fill: #FAEBD7; stroke: #D2691E; stroke-width: 1; }
      .wa-hair-11 { fill: #8B4513; }
      .wa-eyes-11 { fill: #000; }
      .wa-mustache-11 { fill: #A0522D; }
      .wa-hat-11 { fill: #A0522D; stroke: #8B4513; stroke-width: 1; }
    </style>
    <path class="wa-face-11" d="M12 2C8 2 5 5 5 9v6c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-11" d="M5 9c0-2 3-4 7-4s7 2 7 4v4H5v-4z"/>
    <circle class="wa-eyes-11" cx="9" cy="12" r="1.5"/>
    <circle class="wa-eyes-11" cx="15" cy="12" r="1.5"/>
    <path class="wa-mustache-11" d="M9 14h6s-1.5-2-3-2-3 2-3 2z"/>
    <path class="wa-hat-11" d="M17 7s0-3-5-3-5 3-5 3v4h10V7z"/>
    <path d="M8 7s0-3 4-3 4 3 4 3" fill="none" stroke="#8B4513" stroke-width="1"/>
</svg>`,
  },
  {
    id: 8,
    name: "Mystic Alt",
    color: "#D8BFD8",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
        .wa-face-6 {
            fill: #D8BFD8;
            stroke: #BA55D3;
            stroke-width: 1.5;
        }
        .wa-hair-6 {
            fill: #4a148c;
        }
        .wa-eyes-6 {
            fill: #fff;
            stroke: #000;
            stroke-width: 0.5;
        }
       .wa-frown-6 {
          fill: none;
          stroke: #000;
          stroke-width: 1;
       }
        .wa-hat-6 {
            fill: #6a1b9a;
            stroke: #4a148c;
            stroke-width: 1;
        }
    </style>
    <path class="wa-face-6" d="M12 2C8 2 5 5 5 9v6c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-6" d="M5 9v2h14V9c0-2-3-4-7-4s-7 2-7 4z"/>
    <circle class="wa-eyes-6" cx="9" cy="12" r="1.5"/>
    <circle class="wa-eyes-6" cx="15" cy="12" r="1.5"/>
    <path class="wa-frown-6" d="M 8 16 a 8 8 0 0 1 8 0"/>
    <path class="wa-hat-6" d="M 12 4 a 3 3 0 0 1 3 3 h -6 a 3 3 0 0 1 3 -3 z"/>
</svg>`,
  },
  {
    id: 9,
    name: "Ms. Prism",
    color: "#FF69B4",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-20 { fill: #FF69B4; stroke: #fff; stroke-width: 3; }
      .wa-eyes-20 { fill: yellow; }
      .wa-mouth-20 { fill: #fff; }
    </style>
    <polygon class="wa-head-20" points="50,5 95,50 50,95 5,50"/>
    <polygon class="wa-eyes-20" points="35,40 45,40 40,30"/>
    <polygon class="wa-eyes-20" points="65,40 55,40 60,30"/>
    <rect class="wa-mouth-20" x="40" y="70" width="20" height="5"/>
</svg>`,
  },
  {
    id: 10,
    name: "Professor",
    color: "#A98467",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
        .wa-face-0 {
            fill: #F2D7EE;
            stroke: #D4A373;
            stroke-width: 1.5;
        }
        .wa-hair-0 {
            fill: #A98467;
        }
        .wa-eyes-0 {
            fill: #000;
            stroke: none;
        }
        .wa-glasses-0 {
            fill: none;
            stroke: #588157;
            stroke-width: 1;
        }
        .wa-mustache-0 {
            fill: #795548;
            stroke: none;
        }
        .wa-hat-0 {
            fill: #4A1E00;  /* Very Dark Brown */
            stroke: #3D0C02;
            stroke-width: 1;
        }
        .wa-shirt-0 {
            fill: #E5E3E4; /* Very Light Grey */
            stroke: #B1B1B1;
            stroke-width: 1;
        }
        .wa-sweater-0{
            fill:#F4F1DE;
            stroke:#B1B1B1;
            stroke-width:1
        }
    </style>
    <path class="wa-face-0" d="M12 2C8 2 5 5 5 9v6c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-0" d="M5 9c0-2 3-4 7-4s7 2 7 4v2H5v-2z"/>
    <circle class="wa-eyes-0" cx="9" cy="12" r="1"/>
    <circle class="wa-eyes-0" cx="15" cy="12" r="1"/>
    <path class="wa-mustache-0" d="M9 14s1.5-1 3-1 3 1 3 1"/>
    <path class="wa-glasses-0" d="M8 11h3"/>
    <path class="wa-glasses-0" d="M13 11h3"/>
    <path class="wa-glasses-0" d="M11.5 11v2"/>
</svg>`,
  },
  {
    id: 11,
    name: "Count Quackula",
    color: "#FFFACD",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-21 { fill: #FFFACD; }
      .wa-hair-21 { fill: black; }
      .wa-eyes-21 { fill: red; }
      .wa-beak-21 { fill: #FFA500; }
      .wa-beak-line-21 { stroke: black; stroke-width: 1; }
      .wa-fangs-21 { fill: white; }
    </style>
    <circle class="wa-head-21" cx="50" cy="50" r="40"/>
    <path class="wa-hair-21" d="M 35 15 L 50 30 L 65 15 Z"/>
    <circle class="wa-eyes-21" cx="40" cy="45" r="5"/>
    <circle class="wa-eyes-21" cx="60" cy="45" r="5"/>
    <ellipse class="wa-beak-21" cx="50" cy="65" rx="25" ry="12"/>
    <path class="wa-beak-line-21" d="M 25 65 L 75 65"/>
    <path class="wa-fangs-21" d="M 40 71 L 45 80 L 50 71 Z"/>
    <path class="wa-fangs-21" d="M 60 71 L 55 80 L 50 71 Z"/>
</svg>`,
  },
  {
    id: 12,
    name: "Mystic",
    color: "#D8BFD8",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
        .wa-face-3 {
            fill: #D8BFD8;
            stroke: #BA55D3;
            stroke-width: 1.5;
        }
        .wa-hair-3 {
            fill: #4a148c;
        }
        .wa-eyes-3 {
            fill: #fff;
            stroke: #000;
            stroke-width: 0.5;
        }
       .wa-frown-3 {
          fill: none;
          stroke: #000;
          stroke-width: 1;
       }
        .wa-hat-3 {
            fill: #6a1b9a;
            stroke: #4a148c;
            stroke-width: 1;
        }
    </style>
    <path class="wa-face-3" d="M12 2C8 2 5 5 5 9v6c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-3" d="M5 9v2h14V9c0-2-3-4-7-4s-7 2-7 4z"/>
    <circle class="wa-eyes-3" cx="9" cy="12" r="1.5"/>
    <circle class="wa-eyes-3" cx="15" cy="12" r="1.5"/>
    <path class="wa-frown-3" d="M 8 16 a 8 8 0 0 1 8 0"/>
    <path class="wa-hat-3" d="M 12 4 a 3 3 0 0 1 3 3 h -6 a 3 3 0 0 1 3 -3 z"/>
</svg>`,
  },
  {
    id: 13,
    name: "Professor Fizzy",
    color: "#87CEEB",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-16 { fill: #87CEEB; }
      .wa-hair-16 { fill: #FFA500; }
      .wa-goggles-16 { fill: #222; stroke: #fff; stroke-width: 2; }
      .wa-goggle-strap-16 { fill: #fff; }
      .wa-mouth-16 { fill: none; stroke: black; stroke-width: 2; }
    </style>
    <rect class="wa-head-16" x="20" y="30" width="60" height="60" rx="5"/>
    <path class="wa-hair-16" d="M 10 30 C 20 -10, 80 -10, 90 30"/>
    <circle class="wa-goggles-16" cx="40" cy="50" r="12"/>
    <circle class="wa-goggles-16" cx="60" cy="50" r="12"/>
    <rect class="wa-goggle-strap-16" x="48" y="48" width="4" height="4"/>
    <circle class="wa-mouth-16" cx="50" cy="75" r="10"/>
</svg>`,
  },
  {
    id: 14,
    name: "Nature",
    color: "#228B22",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
      .wa-face-4 {
          fill: #FFE4C4;
          stroke: #DEB887;
          stroke-width: 1.5;
      }
      .wa-hair-4 {
          fill: #228B22;
      }
      .wa-eyes-4 {
          fill: #000;
          stroke: none;
      }
      .wa-mouth-4 {
        fill: none;
        stroke: #000;
        stroke-width: 1;
      }
      .wa-hat-4 {
        fill: #556B2F;
        stroke: #228B22;
        stroke-width: 1;
      }
    </style>
    <path class="wa-face-4" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
    <path class="wa-hair-4" d="M12 2a10 10 0 0 1 10 10H2a10 10 0 0 1 10-10z"/>
    <circle class="wa-eyes-4" cx="9" cy="11" r="1.5"/>
    <circle class="wa-eyes-4" cx="15" cy="11" r="1.5"/>
    <path class="wa-mouth-4" d="M 8 15 a 8 8 0 0 0 8 0"/>
    <path class="wa-hat-4" d="M 12 4 a 4 4 0 0 1 4 4 h -8 a 4 4 0 0 1 4 -4 z"/>
</svg>`,
  },
  {
    id: 15,
    name: "Agent Giggles",
    color: "#FFD700",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-face-12 { fill: #FFD700; }
      .wa-eyes-12 { fill: #FFFFFF; }
      .wa-pupils-12 { fill: #000000; }
      .wa-mouth-12 { stroke: #000000; stroke-width: 3; fill: none; }
      .wa-monocle-12 { stroke: #333; stroke-width: 2; fill: none; }
      .wa-monocle-string-12 { stroke: #333; stroke-width: 2; }
      .wa-moustache-12 { fill: #4A2511; }
    </style>
    <circle class="wa-face-12" cx="50" cy="50" r="45"/>
    <circle class="wa-eyes-12" cx="35" cy="40" r="8"/>
    <circle class="wa-pupils-12" cx="35" cy="40" r="4"/>
    <circle class="wa-eyes-12" cx="65" cy="40" r="8"/>
    <circle class="wa-pupils-12" cx="65" cy="40" r="4"/>
    <circle class="wa-monocle-12" cx="65" cy="40" r="12"/>
    <line class="wa-monocle-string-12" x1="77" y1="40" x2="90" y2="30"/>
    <path class="wa-mouth-12" d="M 30 70 Q 50 85, 70 70"/>
    <path class="wa-moustache-12" d="M 25 60 Q 50 55, 75 60 C 65 70, 35 70, 25 60"/>
</svg>`,
  },
  {
    id: 16,
    name: "Wizard",
    color: "#444444",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
      .wa-face-9 { fill: #F8F8FF; stroke: #BDB764; stroke-width: 1; }
      .wa-hair-9 { fill: #D3D3D3; }
      .wa-beard-9 { fill: #D3D3D3; }
      .wa-eyes-9 { fill: #000; }
      .wa-hat-9 { fill: #444444; stroke: #222222; stroke-width: 1; }
      .wa-hat-detail-9 { fill: #FFFF00; }
    </style>
    <path class="wa-face-9" d="M12 2C8 2 5 5 5 9v12c0 4 3 7 7 7s7-3 7-7V9c0-4-3-7-7-7z"/>
    <path class="wa-hair-9" d="M5 9c0-2 3-4 7-4s7 2 7 4v5H5v-5z"/>
    <path class="wa-beard-9" d="M5 14s2-2 7-2 7 2 7 2v4H5v-4z"/>
    <circle class="wa-eyes-9" cx="9" cy="12" r="1.5"/>
    <circle class="wa-eyes-9" cx="15" cy="12" r="1.5"/>
    <path class="wa-hat-9" d="M12 4a8 8 0 0 0-6 6h12a8 8 0 0 0-6-6z"/>
    <polygon class="wa-hat-detail-9" points="12 6 15 8 12 10 9 8"/>
    </svg>`,
  },
  {
    id: 17,
    name: "Sunny",
    color: "#FFD700",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <style>
      .wa-face-2 {
          fill: #FFD700;
          stroke: #FF8C00;
          stroke-width: 1.5;
      }
      .wa-hair-2 {
          fill: #8B4513;
      }
      .wa-eyes-2 {
          fill: #000;
          stroke: none;
      }
      .wa-mouth-2 {
        fill: none;
        stroke: #000;
        stroke-width: 1;
      }
      .wa-hat-2 {
        fill: #8B0000;
        stroke: #8B0000;
        stroke-width: 1;
      }
    </style>
    <path class="wa-face-2" d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
    <path class="wa-hair-2" d="M12 2a10 10 0 0 1 10 10H2a10 10 0 0 1 10-10z"/>
    <circle class="wa-eyes-2" cx="8" cy="10" r="1.5"/>
    <circle class="wa-eyes-2" cx="16" cy="10" r="1.5"/>
    <path class="wa-mouth-2" d="M 8 16 a 8 8 0 0 1 8 0"/>
    <path class="wa-hat-2" d="M12 4a5 5 0 0 1 5 5h-10a5 5 0 0 1 5-5z"/>
</svg>`,
  },
  {
    id: 18,
    name: "Zorp the Alien",
    color: "#90EE90",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-head-17 { fill: #90EE90; }
      .wa-eyes-17 { fill: black; }
      .wa-eye-shine-17 { fill: white; }
      .wa-mouth-17 { stroke: black; stroke-width: 3; stroke-linecap: round; }
    </style>
    <path class="wa-head-17" d="M50 5 C 10 5, 10 95, 50 95 C 90 95, 90 5, 50 5 Z"/>
    <circle class="wa-eyes-17" cx="35" cy="35" r="10"/>
    <circle class="wa-eyes-17" cx="65" cy="35" r="10"/>
    <circle class="wa-eye-shine-17" cx="38" cy="32" r="3"/>
    <circle class="wa-eye-shine-17" cx="68" cy="32" r="3"/>
    <line class="wa-mouth-17" x1="40" y1="70" x2="60" y2="70"/>
</svg>`,
  },
  {
    id: 19,
    name: "Sir Reginald",
    color: "#F08080",
    svg: `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <style>
      .wa-face-15 { fill: #F08080; }
      .wa-eyes-15 { fill: #fff; }
      .wa-pupils-15 { fill: black; }
      .wa-mouth-15 { stroke: black; stroke-width: 3; fill: none; stroke-linecap: round; }
      .wa-eyebrows-15 { stroke: black; stroke-width: 4; fill: none; }
    </style>
    <circle class="wa-face-15" cx="50" cy="50" r="45"/>
    <path class="wa-eyes-15" d="M 25 35 L 45 45 L 25 55 Z"/>
    <path class="wa-eyes-15" d="M 75 35 L 55 45 L 75 55 Z"/>
    <circle class="wa-pupils-15" cx="35" cy="45" r="3"/>
    <circle class="wa-pupils-15" cx="65" cy="45" r="3"/>
    <path class="wa-mouth-15" d="M 30 75 C 40 65, 60 65, 70 75"/>
    <path class="wa-eyebrows-15" d="M 25 25 Q 35 15, 45 25"/>
    <path class="wa-eyebrows-15" d="M 55 25 Q 65 35, 75 25"/>
</svg>`,
  }
];

/**
 * Get an icon by its ID
 * @param iconId The ID of the icon to retrieve
 * @returns The requested icon, or the first icon if the ID is not found
 */
export function getPlayerIconById(iconId: number): PlayerIcon {
  const icon = playerIcons.find(icon => icon.id === iconId);
  return icon || playerIcons[0]; // Return the first icon as fallback
}
